import {
  DIAS_AVISO_DEFAULT,
  MONEDA,
  dominioCreateSchema,
  dominioUpdateSchema,
  sumarAnios,
} from "@uirtus/shared";
import { useState, type FormEvent } from "react";
import type { Dominio } from "../lib/dominios";
import { useProveedoresDominio } from "../lib/dominios";
import { SelectorCliente, type ClienteResumen } from "./selector-cliente";
import { AreaTexto, Aviso, Boton, Campo, Entrada, Seleccion } from "./ui";

interface Props {
  dominio?: Dominio;
  /** Cliente preseleccionado (alta desde la ficha del cliente) o el actual al editar. */
  clienteInicial?: ClienteResumen;
  enviando?: boolean;
  errorServidor?: string | null;
  alEnviar: (datos: Record<string, unknown>) => void;
  alCancelar: () => void;
}

const soloNumeroDecimal = (v: string): string => v.replace(/[^\d.]/g, "");

const textoAvisos = (dias: readonly number[]): string => dias.join(", ");

function leerAvisos(texto: string): number[] | null {
  const partes = texto
    .split(/[,\s]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (partes.length === 0) return null;
  const nums = partes.map(Number);
  return nums.every((n) => Number.isInteger(n) && n >= 0 && n <= 365) ? nums : null;
}

export function FormularioDominio({
  dominio,
  clienteInicial,
  enviando,
  errorServidor,
  alEnviar,
  alCancelar,
}: Props) {
  const { data: proveedores } = useProveedoresDominio();

  const [cliente, setCliente] = useState<ClienteResumen | null>(clienteInicial ?? null);
  const [nombre, setNombre] = useState(dominio?.nombre ?? "");
  const [proveedorId, setProveedorId] = useState(dominio?.proveedorId ?? "");
  const [cuenta, setCuenta] = useState(dominio?.cuentaProveedor ?? "");
  const [registro, setRegistro] = useState(dominio?.fechaRegistro ?? "");
  const [vencimiento, setVencimiento] = useState(dominio?.fechaVencimiento ?? "");
  const [autoRenovacion, setAutoRenovacion] = useState(dominio?.autoRenovacion ?? false);
  const [costo, setCosto] = useState(dominio?.costoRenovacion ?? "");
  const [moneda, setMoneda] = useState<string>(dominio?.moneda ?? "MXN");
  const [recordatorios, setRecordatorios] = useState(dominio?.recordatoriosActivos ?? true);
  const [avisos, setAvisos] = useState(textoAvisos(dominio?.diasAviso ?? DIAS_AVISO_DEFAULT));
  const [correos, setCorreos] = useState(dominio?.correosAviso ?? "");
  const [notas, setNotas] = useState(dominio?.notas ?? "");
  const [errores, setErrores] = useState<Record<string, string>>({});

  function enviar(e: FormEvent) {
    e.preventDefault();

    const diasAviso = leerAvisos(avisos);
    if (!diasAviso) {
      setErrores({ diasAviso: "Números de 0 a 365 separados por coma, p.ej. 60, 30, 15, 7, 1" });
      return;
    }

    const base = {
      nombre: nombre.trim(),
      proveedorId: proveedorId || (dominio ? null : undefined),
      cuentaProveedor: cuenta.trim(),
      fechaRegistro: registro,
      fechaVencimiento: vencimiento,
      autoRenovacion,
      costoRenovacion: costo.trim(),
      moneda,
      recordatoriosActivos: recordatorios,
      diasAviso,
      correosAviso: correos.trim(),
      notas: notas.trim(),
    };
    const datos = dominio ? base : { ...base, clienteId: cliente?.id };

    const esquema = dominio ? dominioUpdateSchema : dominioCreateSchema;
    const resultado = esquema.safeParse(datos);
    if (!resultado.success) {
      const porCampo: Record<string, string> = {};
      for (const issue of resultado.error.issues) {
        const campo = String(issue.path[0] ?? "");
        if (campo && !porCampo[campo]) porCampo[campo] = issue.message;
      }
      setErrores(porCampo);
      return;
    }

    alEnviar(datos);
  }

  return (
    <form onSubmit={enviar} className="space-y-5 p-5">
      {errorServidor && <Aviso>{errorServidor}</Aviso>}

      {!dominio ? (
        <SelectorCliente valor={cliente} alSeleccionar={setCliente} error={errores["clienteId"]} />
      ) : (
        <Campo etiqueta="Cliente">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-800/50">
            {clienteInicial?.razonSocial}
          </div>
        </Campo>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo etiqueta="Dominio" requerido error={errores["nombre"]}>
          <Entrada
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="acme.com.mx"
            className="font-mono"
            error={!!errores["nombre"]}
          />
        </Campo>
        <Campo etiqueta="Proveedor" error={errores["proveedorId"]} ayuda="Dónde está contratado">
          <Seleccion value={proveedorId} onChange={(e) => setProveedorId(e.target.value)}>
            <option value="">Sin proveedor</option>
            {(proveedores ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </Seleccion>
        </Campo>
      </div>

      <Campo
        etiqueta="Cuenta en el proveedor"
        error={errores["cuentaProveedor"]}
        ayuda="Usuario o correo con el que se entra al panel. Nunca captures la contraseña aquí."
      >
        <Entrada value={cuenta} onChange={(e) => setCuenta(e.target.value)} />
      </Campo>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo etiqueta="Fecha de registro" error={errores["fechaRegistro"]}>
          <Entrada type="date" value={registro} onChange={(e) => setRegistro(e.target.value)} />
        </Campo>
        <Campo etiqueta="Vencimiento" requerido error={errores["fechaVencimiento"]}>
          <div className="flex gap-2">
            <Entrada
              type="date"
              value={vencimiento}
              onChange={(e) => setVencimiento(e.target.value)}
              error={!!errores["fechaVencimiento"]}
            />
            {registro && !dominio && (
              <Boton
                type="button"
                variante="secundario"
                onClick={() => setVencimiento(sumarAnios(registro, 1))}
                title="Un año después del registro"
              >
                +1 año
              </Boton>
            )}
          </div>
        </Campo>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          etiqueta="Costo de renovación (sin IVA)"
          error={errores["costoRenovacion"]}
          ayuda="Al cliente se le avisa con el IVA del 16% sumado."
        >
          <Entrada
            value={costo}
            onChange={(e) => setCosto(soloNumeroDecimal(e.target.value))}
            inputMode="decimal"
            placeholder="0.00"
            className="font-mono"
          />
        </Campo>
        <Campo etiqueta="Moneda" error={errores["moneda"]}>
          <Seleccion value={moneda} onChange={(e) => setMoneda(e.target.value)}>
            {Object.entries(MONEDA)
              .filter(([c]) => c !== "XXX")
              .map(([c, nombreMoneda]) => (
                <option key={c} value={c}>
                  {c} · {nombreMoneda}
                </option>
              ))}
          </Seleccion>
        </Campo>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={autoRenovacion}
          onChange={(e) => setAutoRenovacion(e.target.checked)}
          className="rounded border-slate-300 text-marca-600 focus:ring-marca-500"
        />
        <span>
          Tiene renovación automática en el proveedor
          <span className="ml-1 text-xs text-slate-500">
            (el aviso al cliente lo dice y no le pide confirmar)
          </span>
        </span>
      </label>

      <fieldset className="space-y-4 rounded-lg border border-slate-200 p-4 dark:border-slate-800">
        <legend className="px-1 text-sm font-medium">Recordatorios al cliente</legend>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={recordatorios}
            onChange={(e) => setRecordatorios(e.target.checked)}
            className="rounded border-slate-300 text-marca-600 focus:ring-marca-500"
          />
          Avisar por correo antes del vencimiento
        </label>

        {recordatorios && (
          <>
            <Campo
              etiqueta="Días de aviso"
              error={errores["diasAviso"]}
              ayuda="Cuántos días antes del vencimiento se manda cada correo."
            >
              <Entrada
                value={avisos}
                onChange={(e) => setAvisos(e.target.value)}
                className="font-mono"
                error={!!errores["diasAviso"]}
              />
            </Campo>
            <Campo
              etiqueta="Enviar a"
              error={errores["correosAviso"]}
              ayuda="Opcional. Vacío = correo del cliente y de su contacto principal."
            >
              <Entrada
                value={correos}
                onChange={(e) => setCorreos(e.target.value)}
                placeholder="ti@cliente.com, admin@cliente.com"
                error={!!errores["correosAviso"]}
              />
            </Campo>
          </>
        )}
      </fieldset>

      <Campo etiqueta="Notas" error={errores["notas"]}>
        <AreaTexto rows={3} value={notas} onChange={(e) => setNotas(e.target.value)} />
      </Campo>

      <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
        <Boton type="button" variante="secundario" onClick={alCancelar}>
          Cancelar
        </Boton>
        <Boton type="submit" disabled={enviando}>
          {enviando ? "Guardando…" : dominio ? "Guardar cambios" : "Registrar dominio"}
        </Boton>
      </div>
    </form>
  );
}
